import { z } from "zod";

/** Stable public codes. Status and safe default copy have one source of truth. */
export const ERROR_CATALOG = {
  BAD_REQUEST: { status: 400, message: "We couldn't understand that request. Please try again." },
  UNAUTHORIZED: { status: 401, message: "Please sign in again to continue." },
  FORBIDDEN: { status: 403, message: "You don't have access to this workspace or action." },
  FORBIDDEN_ORIGIN: { status: 403, message: "Please open the app from its trusted address." },
  WORKSPACE_REQUIRED: { status: 403, message: "Choose a workspace to continue." },
  WORKSPACE_ACCESS_DENIED: {
    status: 403,
    message:
      "Your access to this workspace has changed. Choose another workspace or contact its administrator.",
  },
  NOT_FOUND: { status: 404, message: "We couldn't find what you're looking for." },
  CONFLICT: {
    status: 409,
    message: "This has changed since you opened it. Refresh and try again.",
  },
  /** Invitations: the link is for a different address than the one signed in. */
  WRONG_ADDRESS: {
    status: 403,
    message: "This invitation was sent to a different address than the one you're signed in with.",
  },
  /** Invitations: the link was never issued, was taken back, or has already been used. */
  INVITATION_UNAVAILABLE: {
    status: 404,
    message: "This invitation isn't available. Ask the person who invited you to send a new one.",
  },
  /** Invitations: the link was good once, and its seven days are up. */
  INVITATION_EXPIRED: {
    status: 410,
    message: "This invitation has expired. Ask the person who invited you to send a new one.",
  },
  /** Invitations: an account belongs to one organization at a time. */
  ALREADY_IN_ORGANIZATION: {
    status: 409,
    message: "This account already belongs to an organization. An account can be in one at a time.",
  },
  /** Roles and removal: a workspace always keeps at least one owner. */
  LAST_OWNER: {
    status: 409,
    message: "A workspace needs at least one owner. Make someone else an owner first.",
  },
  /** A save that would write the value already stored, so there is nothing to save. */
  NOTHING_TO_CHANGE: {
    status: 409,
    message: "That's already the current value, so nothing changed.",
  },
  CONNECTION_NEEDS_RENEWAL: {
    status: 409,
    message: "This connection needs its access renewed before it can be used.",
  },
  /** Chats: the message is longer than a message may be (`MESSAGE_MAX_CHARS`). */
  MESSAGE_TOO_LARGE: {
    status: 413,
    message: "That message is too long. Shorten it and try again.",
  },
  /** Chats: the transcript no longer fits the model's context window, so the chat cannot go on. */
  CHAT_TOO_LONG: {
    status: 413,
    message: "This chat has grown too long to continue. Start a new chat.",
  },
  /** Chats: the model ran out of room before the answer was whole; the half is not kept as an answer. */
  ANSWER_CUT_OFF: {
    status: 502,
    message:
      "Abacor ran out of room before it finished writing this answer. Try again, or ask for less at once.",
  },
  /** Chats: the model ended its last turn with nothing to say and nothing left to do; no empty answer is kept. */
  EMPTY_ANSWER: {
    status: 502,
    message: "Abacor didn't write an answer this time. Try again.",
  },
  /** Chats: a stop or abort named a run that has already finished or been replaced. */
  RUN_NOT_ACTIVE: { status: 409, message: "Abacor isn't working on that any more." },
  /** Chats: the question was answered, canceled or timed out before this answer arrived. */
  REQUEST_NOT_PENDING: {
    status: 409,
    message: "That question has already been answered or has closed.",
  },
  /** Chats: a run used up its loop turns, time or cost (its manifest's limits) and was stopped. */
  RUN_LIMIT_REACHED: {
    status: 429,
    message:
      "Abacor stopped because this took longer or cost more than one run allows. Send a message to continue.",
  },
  /** Files: the type is not one a chat accepts (`FILE_TYPES`). */
  FILE_TYPE_NOT_ALLOWED: {
    status: 415,
    message: "That kind of file can't be attached. Use a PDF, an image or a text file.",
  },
  /** Files: the file is over the size a chat accepts (`FILE_MAX_BYTES`). */
  FILE_TOO_LARGE: { status: 413, message: "That file is too large. The limit is 25 MB." },
  /** Files: a message may carry no more files than `FILES_PER_MESSAGE_MAX`. */
  FILE_LIMIT: { status: 422, message: "You can attach up to 10 files to a message." },
  /** Files: too many uploads started and not finished (`PENDING_UPLOADS_MAX`). */
  UPLOAD_RATE_LIMIT: {
    status: 429,
    message: "Too many uploads at once. Please wait a moment and try again.",
  },
  /** Files: the file's bytes have not arrived, so it cannot be completed or attached yet. */
  FILE_NOT_READY: { status: 409, message: "That file hasn't finished uploading yet." },
  /** Processes: a newer version went live since this draft was cut, so it must catch up first. */
  DRAFT_BEHIND_LIVE: {
    status: 409,
    message: "A newer version went live after this draft began. Bring it in, then release.",
  },
  /**
   * Processes: only the person who started the draft, or an admin, changes it (decision
   * `2026-11-02-one-open-draft-changed-by-its-creator-or-an-admin`).
   */
  DRAFT_NOT_YOURS: {
    status: 403,
    message: "Only the person who started this draft, or an admin, can change it.",
  },
  /** Processes: a playbook has one open draft, so another is not started beside it. */
  DRAFT_ALREADY_OPEN: {
    status: 409,
    message: "This playbook already has a draft. Wait until it's published or discarded.",
  },
  /** Processes: only the person who made the playbook, or an admin, starts its next draft. */
  PROCESS_NOT_YOURS: {
    status: 403,
    message: "Only the person who made this playbook, or an admin, can start a new draft of it.",
  },
  /** Processes: an earlier catch-up left conflicts a person has not decided yet. */
  MERGE_UNDECIDED: {
    status: 409,
    message: "Decide the steps this draft and the live version both changed, then continue.",
  },
  /** Playbooks: a person's value for a step's slot is outside the range the step allows. */
  SLOT_OUT_OF_RANGE: {
    status: 422,
    message: "That's outside what this step allows. Ask Abacor to change it.",
  },
  /**
   * Playbooks and tables: the account doesn't fit where it was set. A step takes a workspace
   * account or its draft creator's own, never another member's; a login column's cell takes a
   * workspace account only (decision
   * `2026-11-02-a-step-names-its-accounts-and-sets-them-up-in-place`, item 4).
   */
  ACCOUNT_NOT_ALLOWED: {
    status: 422,
    message: "That account can't be used here. Pick another one.",
  },
  /**
   * Playbooks: a step names an app this workspace can't connect (decision
   * `2026-11-03-the-builder-finds-connectable-apps-by-search`).
   */
  APP_NOT_OFFERED: {
    status: 422,
    message: "This workspace can't connect that app. Choose another way to do this step.",
  },
  /** Playbooks: Abacor's reply is writing the draft, so people wait until it ends. */
  REPLY_WRITING: {
    status: 409,
    message: "Abacor is still writing this reply. You can change the draft when it finishes.",
  },
  /** Playbooks: an undo named a change that is no longer the newest, or a revision since moved on. */
  UNDO_NOT_NEWEST: {
    status: 409,
    message:
      "The draft just changed, so the newest change is a different one. Check it, then undo.",
  },
  /** Playbooks: a redo named a change a newer one has since dropped. */
  REDO_UNAVAILABLE: {
    status: 409,
    message: "A newer change landed, so there's nothing to redo.",
  },
  /** Playbooks: the newest change is a catch-up or a pin, which Undo stops at. */
  UNDO_FLOOR: {
    status: 409,
    message: "This change can't be undone here. Ask Abacor to reverse it, or discard the draft.",
  },
  /** Processes: the draft's order and the live version's cannot both hold. */
  MERGE_ORDER_CONFLICT: {
    status: 409,
    message:
      "This draft's order can't be combined with the live version. Change it, then bring it in.",
  },
  /** Recordings: a stretch to remove holds more captured parts than one removal can carry. */
  STRETCH_TOO_LARGE: {
    status: 422,
    message: "That stretch holds too much to remove at once. Remove a shorter stretch.",
  },
  /** Tables: another table of the workspace already has this name. */
  TABLE_NAME_TAKEN: { status: 409, message: "Another table already has that name." },
  /** Teammates: another teammate of the workspace already has this name. */
  TEAMMATE_NAME_TAKEN: { status: 409, message: "Another teammate already has that name." },
  /** Teammates: a teammate is removed only once it owns no playbooks. */
  TEAMMATE_OWNS_PLAYBOOKS: {
    status: 409,
    message: "This teammate still owns playbooks. Move them to another teammate, then remove it.",
  },
  /** Tables: another row of the table already has these key values. */
  TABLE_KEY_TAKEN: { status: 409, message: "Another row already has those key values." },
  /** Processes: the table has more rows than a pin, and so a release, can carry. */
  TABLE_TOO_LARGE_TO_PIN: {
    status: 422,
    message: "This table has more rows than a playbook version can carry. Split it, then pin it.",
  },
  /** Tables: a live version of a process reads the column, so it can't be removed or retyped. */
  COLUMN_IN_USE: {
    status: 409,
    message: "A live playbook reads this column. Change the playbook first, then the column.",
  },
  /** Tables: a retype that is not one of the safe conversions (concept map item 9). */
  COLUMN_RETYPE_UNSAFE: {
    status: 422,
    message:
      "A column can only become text, a count can become a number, and a number an amount with a currency. Make a new column and fill it instead.",
  },
  /** Tables: a choice column would lose an option some rows still use. */
  CHOICE_OPTION_IN_USE: {
    status: 409,
    message: "Some rows still use that option. Change those rows first, then remove it.",
  },
  /** Processes: a release check failed, or a question the release cannot run without is open. */
  RELEASE_BLOCKED: {
    status: 409,
    message: "Settle what this version can't run without, then release.",
  },
  /** Cases: the process has no live version, so no case can run on it yet. */
  PROCESS_NOT_LIVE: {
    status: 409,
    message: "This playbook has no live version yet. Release it, then start a case.",
  },
  /** Cases: the playbook's version does not say what one case is, so no case can run on it. */
  CASE_UNIT_MISSING: {
    status: 409,
    message:
      "This playbook doesn't say what one case is yet. Add that, publish, then start a case.",
  },
  /**
   * Cases: what a case was started with does not fit the fields its playbook's Starts when hands a
   * case: one is missing, of the wrong kind, unknown, or made up for a live case.
   */
  START_INPUT_MISMATCH: {
    status: 422,
    message: "What this case starts with doesn't match what the playbook expects.",
  },
  /** Cases: a step that only looks tried to change or send something. */
  STEP_LOOKS_ONLY: {
    status: 409,
    message: "This step only looks. It can't change or send anything.",
  },
  /**
   * Cases: a step was settled done while a required output is not recorded, or (a record, a
   * message or a sign-off) not confirmed.
   */
  STEP_OUTPUTS_MISSING: {
    status: 409,
    message: "Record everything this step asks for, then mark it done.",
  },
  /** Cases: the case is closed and sealed; only a flag, a correction or erasure is taken. */
  CASE_SEALED: {
    status: 409,
    message: "This case is closed. Add a correction instead.",
  },
  /** Cases: only a try of the draft can be tried again or stopped from the chat. */
  CASE_NOT_A_TRY: {
    status: 409,
    message: "This is a live case, not a try of the draft, so it can't be tried again.",
  },
  /** Cases: a case cannot complete while one of its steps is still running. */
  CASE_STEPS_OPEN: {
    status: 409,
    message: "Some steps of this case are still running. Settle them, then close it.",
  },
  /** Cases: something the process delivers is missing or not confirmed, so it cannot complete. */
  CASE_NOT_DELIVERED: {
    status: 409,
    message:
      "Some of what this case delivers is missing or unconfirmed. Finish it, or close it with deviations.",
  },
  /** Cases: another effect already holds this business key ("one sales order per PO"). */
  EFFECT_KEY_TAKEN: {
    status: 409,
    message: "This has been done already for this case's subject, so it isn't done twice.",
  },
  /** Cases: an earlier effect of this step went out and how it ended is not known yet. */
  EFFECT_OUTCOME_UNKNOWN: {
    status: 409,
    message: "An earlier attempt may have gone through. Check the system first, then try again.",
  },
  /** Cases: the approval given is not for exactly what this effect sends. */
  EFFECT_NOT_APPROVED: {
    status: 409,
    message: "The approval doesn't match what this step would send, so nothing was sent.",
  },
  /** Cases: an effect of the case went out and has not ended, so it cannot complete. */
  CASE_EFFECTS_OPEN: {
    status: 409,
    message: "Something this case sent hasn't been confirmed yet. Check it, then close the case.",
  },
  /** Cases: the step changed or sent something, so only a person may run it again. */
  STEP_HAS_EFFECTS: {
    status: 409,
    message: "This step already changed or sent something. A person decides whether it runs again.",
  },
  /** Cases: an open question holds this step for this case until it is answered or settled. */
  STEP_HELD: {
    status: 409,
    message: "A question holds this step. Answer it, or settle it for this case, then start it.",
  },
  /** Cases: a table or column the step reads was removed after its version was released. */
  STEP_READ_MISSING: {
    status: 409,
    message: "A table or column this step reads has been removed, so it can't start.",
  },
  /** Cases: a live table the step reads has more rows than one step can freeze. */
  TABLE_TOO_LARGE_TO_READ: {
    status: 422,
    message: "This table has more rows than a step can read at once. Split it, then try again.",
  },
  /** Recordings: the chat already has a recording that is live or paused. */
  RECORDING_IN_PROGRESS: {
    status: 409,
    message: "This chat already has a recording going. Finish it to start another.",
  },
  /** Recordings: the recording was finished or stopped, so it takes nothing more. */
  RECORDING_ENDED: {
    status: 409,
    message: "This recording has ended. Start a new one to keep going.",
  },
  /** Recordings: a live or paused recording cannot be deleted until it has ended. */
  RECORDING_NOT_ENDED: {
    status: 409,
    message: "This recording is still going. End it from its island, then delete it.",
  },
  VALIDATION_ERROR: { status: 422, message: "Check the highlighted fields and try again." },
  RATE_LIMITED: { status: 429, message: "Too many attempts. Please wait a moment and try again." },
  INTERNAL_ERROR: { status: 500, message: "Something went wrong. Please try again." },
  SERVICE_UNAVAILABLE: {
    status: 503,
    message: "The service is temporarily unavailable. Please try again shortly.",
  },
  PROVIDER_UNAVAILABLE: {
    status: 503,
    message: "The connected app isn't responding. Please try again shortly.",
  },
  AUTH_UNAVAILABLE: {
    status: 503,
    message: "Sign-in is temporarily unavailable. Please try again shortly.",
  },
  /** Operations panel only: a deletion would leave live tokens at Composio, so it refuses. */
  CONNECTIONS_UNPURGEABLE: {
    status: 503,
    message:
      "This removes connected app accounts, but the panel's Composio key is missing or belongs to another Composio project. Set COMPOSIO_API_KEY for the panel to the regional API's key and try again.",
  },
} as const;

export type ErrorCode = keyof typeof ERROR_CATALOG;
export type ErrorStatus = (typeof ERROR_CATALOG)[ErrorCode]["status"];
export const errorCodeSchema = z.enum(Object.keys(ERROR_CATALOG) as [ErrorCode, ...ErrorCode[]]);

/** Matches the bounded, printable identifiers accepted/generated by Hono request-id. */
export const requestIdSchema = z
  .string()
  .min(1)
  .max(255)
  .regex(/^[A-Za-z0-9_=-]+$/);

export const VALIDATION_ISSUE_CATALOG = {
  REQUIRED: "This field is required.",
  INVALID_FORMAT: "Enter a value in the correct format.",
  TOO_SHORT: "This value is too short.",
  TOO_LONG: "This value is too long.",
  OUT_OF_RANGE: "Enter a value within the allowed range.",
  INVALID_VALUE: "Check this value and try again.",
} as const;
export type ValidationIssueCode = keyof typeof VALIDATION_ISSUE_CATALOG;
export const validationIssueCodeSchema = z.enum(
  Object.keys(VALIDATION_ISSUE_CATALOG) as [ValidationIssueCode, ...ValidationIssueCode[]],
);
export const validationIssueSchema = z.object({
  path: z.array(z.union([z.string().min(1).max(100), z.number().int().nonnegative()])).max(10),
  code: validationIssueCodeSchema,
  message: z.string().min(1).max(500),
});
export type ValidationIssue = z.infer<typeof validationIssueSchema>;
const validationIssuesSchema = z.array(validationIssueSchema).min(1).max(50);

export const errorResponseSchema = z.object({
  error: z
    .object({
      code: errorCodeSchema,
      message: z.string().min(1).max(500),
      requestId: requestIdSchema,
      issues: validationIssuesSchema.optional(),
    })
    .refine((error) => error.issues === undefined || error.code === "VALIDATION_ERROR", {
      message: "Field issues are only valid for validation errors.",
      path: ["issues"],
    }),
});
export type ErrorResponse = z.infer<typeof errorResponseSchema>;
export type ErrorResponseForCode<Code extends ErrorCode> = {
  error: Omit<ErrorResponse["error"], "code"> & { code: Code };
};
export type ErrorCodeForStatus<Status extends ErrorStatus> = {
  [Code in ErrorCode]: (typeof ERROR_CATALOG)[Code]["status"] extends Status ? Code : never;
}[ErrorCode];
export type ErrorResponseForStatus<Status extends ErrorStatus> = ErrorResponseForCode<
  ErrorCodeForStatus<Status>
>;

/** Paths must name public input fields; never include submitted values or raw validator messages. */
export function createValidationIssue(
  path: ValidationIssue["path"],
  code: ValidationIssueCode,
): ValidationIssue {
  return validationIssueSchema.parse({ path, code, message: VALIDATION_ISSUE_CATALOG[code] });
}

/** No arbitrary message override: exception text and submitted values stay out of the wire format. */
export function createErrorResponse<Code extends ErrorCode>(
  code: Code,
  requestId: string,
  issues?: Code extends "VALIDATION_ERROR" ? ValidationIssue[] : never,
): ErrorResponseForCode<Code> {
  if (issues !== undefined && code !== "VALIDATION_ERROR") {
    throw new Error("Field issues require VALIDATION_ERROR.");
  }
  return {
    error: {
      code,
      message: ERROR_CATALOG[code].message,
      requestId: requestIdSchema.parse(requestId),
      ...(issues === undefined
        ? {}
        : {
            issues: validationIssuesSchema.parse(
              issues.map((issue) => createValidationIssue(issue.path, issue.code)),
            ),
          }),
    },
  };
}

/** What an error envelope carries besides its code and copy; an oRPC error's `data`. */
export const errorDataSchema = z.object({
  requestId: requestIdSchema,
  issues: validationIssuesSchema.optional(),
});
export type ErrorData = z.infer<typeof errorDataSchema>;

/**
 * Catalog entries as a contract's typed errors (oRPC's `.errors()` map): each code keeps its
 * status and safe copy, so a typed client and the OpenAPI document agree with the envelope.
 */
export function catalogErrors<const Codes extends readonly ErrorCode[]>(codes: Codes) {
  return Object.fromEntries(
    codes.map((code) => [
      code,
      {
        status: ERROR_CATALOG[code].status,
        message: ERROR_CATALOG[code].message,
        data: errorDataSchema,
      },
    ]),
  ) as {
    [Code in Codes[number]]: {
      status: (typeof ERROR_CATALOG)[Code]["status"];
      message: string;
      data: typeof errorDataSchema;
    };
  };
}

export function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === "string" && Object.hasOwn(ERROR_CATALOG, value);
}
